import { Add, Delete } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { EntitySelector } from "modals/EntitySelector";
import { FC, useState } from "react";
import { getFilteredList } from "utils/Array";

type ListWithEntitySelectorProps = {
  label?: string;
  elements: any[];
  onChangeElements: (value: any[]) => void;
  idField: string;
  entityName: string;
  entityList: any[];
  maxElements?: number;
  displayField?: string;
  displayFieldSecondary?: string | null;
  error?: string;
};

function pluralizeEntityName(entityName: string): string {
  const name = entityName.trim().toLowerCase();
  if (!name) return name;
  if (name === "species") {
    return name;
  }
  if (/(s|x|z|ch|sh)$/.test(name) && !name.endsWith("s")) {
    return `${name}es`;
  }
  if (/[^aeiou]y$/i.test(name)) {
    return `${name.slice(0, -1)}ies`;
  }
  if (name.endsWith("s")) {
    return name;
  }
  return `${name}s`;
}

export const ListWithEntitySelector: FC<ListWithEntitySelectorProps> = ({
  label,
  elements,
  onChangeElements,
  idField,
  entityName,
  entityList,
  maxElements = 0,
  displayField = "name",
  displayFieldSecondary = null,
  error = "",
}) => {
  const [openEntitySelector, setOpenEntitySelector] = useState<boolean>(false);
  const emptyMessage = `No ${pluralizeEntityName(entityName)} selected yet. Click Add ${entityName} to get started.`;

  return (
    <Box>
      {label && (
        <Typography variant="h6" mb={2}>
          {label}
        </Typography>
      )}
      <Stack spacing={2}>
        {elements.length === 0 && (
          <Box pt={1}>
            <Alert severity="info">{emptyMessage}</Alert>
          </Box>
        )}
        {elements.length > 0 && (
          <List>
            {elements.map((element, i) => (
              <ListItem
                key={element[idField]}
                sx={{
                  borderBottom:
                    i === elements.length - 1
                      ? "none"
                      : "1px solid rgba(0,0,0,0.2)",
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => {
                      onChangeElements(
                        elements.filter(
                          (data) => data[idField] !== element[idField],
                        ),
                      );
                    }}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={element[displayField]}
                  secondary={
                    displayFieldSecondary
                      ? element[displayFieldSecondary]
                      : null
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
        <Box></Box>
        {(maxElements === 0 || maxElements > elements.length) && (
          <Button
            variant="outlined"
            onClick={() => {
              setOpenEntitySelector(true);
            }}
            startIcon={<Add />}
          >
            Add {entityName}
          </Button>
        )}
      </Stack>

      {openEntitySelector && (
        <EntitySelector
          open={openEntitySelector}
          onClose={() => {
            setOpenEntitySelector(false);
          }}
          title={`Select ${entityName}`}
          filterCriteria={(search, data) =>
            data[displayField].toLowerCase().includes(search.toLowerCase())
          }
          listEntities={getFilteredList(entityList, elements, idField)}
          onSelect={(selected: any) => {
            onChangeElements([...elements, selected]);
          }}
          displayField={displayField}
          displayFieldSecondary={displayFieldSecondary}
          keyField={idField}
        />
      )}
      {error !== "" && (
        <Typography
          variant="body2"
          sx={{ marginTop: 1, paddingLeft: 2 }}
          color="error"
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
